import {
  ArrowLeft,
  Check,
  ChevronRight,
  CircleStop,
  Copy,
  FolderOpen,
  Import,
  List,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Play,
  Plus,
  Power,
  ShieldAlert,
  ShieldCheck,
  TerminalSquare,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { DesktopClient } from "@orni/domain-typescript";
import type { AutoContinuePolicy, Session, Snapshot } from "../domain";
import { CommandDialog, ConfirmDialog } from "../components/Ui";
import { ProviderIcon } from "../components/ProviderIcon";
import { localizeError, useI18n } from "../i18n";
import { NativeSessionView } from "../native-terminal/NativeSessionView";
import type { ProviderTerminalQuickCommand } from "../sdk/types";

interface SessionWorkspaceProps {
  session: Session;
  snapshot: Snapshot;
  client: DesktopClient;
  insertText?: { text: string; nonce: number } | null;
  onBack: () => void;
  onState: (state: Session["state"]) => Promise<void> | void;
  onRename: (title: string) => void;
  onNewSessionCurrentDir: (
    provider: Session["provider"],
    workspacePath: string,
  ) => Promise<void> | void;
  onOpenSessionSwitcher?: () => void;
  onChooseSession?: (id: string) => void;
  recentSessions?: readonly Session[];
  onToggleSessionList?: () => void;
  sessionListCollapsed?: boolean;
  onOpenCreate?: () => void;
  onOpenImport?: () => void;
  onDelete?: () => void;
  /** Legacy hidden route compatibility; the new shell has no visible hosting button. */
  onAutoContinuePolicy?: (policy: AutoContinuePolicy) => void;
  providerQuickCommands?: readonly ProviderTerminalQuickCommand[];
}

/** The Session product surface is the Provider's real PTY. */
export function SessionWorkspace({
  session,
  snapshot,
  client,
  insertText,
  onBack,
  onState,
  onRename,
  onNewSessionCurrentDir,
  onOpenSessionSwitcher,
  onChooseSession,
  recentSessions = [],
  onToggleSessionList,
  sessionListCollapsed = false,
  onOpenCreate,
  onOpenImport,
  onDelete,
  providerQuickCommands = [],
}: SessionWorkspaceProps) {
  const { t } = useI18n();
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(session.title);
  const [pendingState, setPendingState] = useState<"stop" | "restart" | null>(null);
  const [lifecycleRequest, setLifecycleRequest] = useState<"starting" | "stopping" | null>(null);
  const [localCommandOpen, setLocalCommandOpen] = useState(false);
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [directoryDialog, setDirectoryDialog] = useState<"view" | "new" | null>(null);
  const [directoryPath, setDirectoryPath] = useState(session.workspacePath.trim());
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [directoryBusy, setDirectoryBusy] = useState(false);

  const isExecutionSession = session.workRole === "execution";
  const toggleSessionList = onToggleSessionList ?? onBack;
  const isStartRequested = lifecycleRequest === "starting";
  const isSessionStarting = isStartRequested
    || Boolean(session.launchingWork)
    || session.state === "starting"
    || session.id.startsWith("launching_");
  const isCreationFailure = session.id.startsWith("launching_") && session.state === "error";
  const isSessionInactive = !isStartRequested && isInactiveSessionState(session.state);
  const canStop = !isExecutionSession && ["starting", "running", "waiting"].includes(session.state);

  useEffect(() => {
    if (!renaming) setNameDraft(session.title);
  }, [renaming, session.title]);
  useEffect(() => {
    if (lifecycleRequest === "starting"
      && ["starting", "running", "waiting"].includes(session.state)) {
      setLifecycleRequest(null);
    }
    if (lifecycleRequest === "stopping" && session.state === "stopped") {
      setLifecycleRequest(null);
    }
  }, [lifecycleRequest, session.state]);
  useEffect(() => {
    setRenaming(false);
    setNameDraft(session.title);
    setPendingState(null);
    setLifecycleRequest(null);
    setLocalCommandOpen(false);
    setSessionMenuOpen(false);
    setDeleteConfirmOpen(false);
    setDirectoryDialog(null);
    setDirectoryPath(session.workspacePath.trim());
    setDirectoryError(null);
    setDirectoryBusy(false);
  }, [session.id]);

  const saveName = () => {
    const value = nameDraft.trim();
    if (!value) return;
    setRenaming(false);
    if (value !== session.title) onRename(value);
  };
  const requestState = async (state: Session["state"]) => {
    setLifecycleRequest(state === "running" ? "starting" : "stopping");
    try {
      await onState(state);
    } catch {
      setLifecycleRequest(null);
    }
  };
  const openDirectoryDialog = (mode: "view" | "new") => {
    setSessionMenuOpen(false);
    setDirectoryDialog(mode);
    setDirectoryError(null);
    const cached = session.workspacePath.trim();
    if (cached) {
      setDirectoryPath(cached);
      return;
    }
    setDirectoryLoading(true);
    void client.getSessionWorkspace(session.id)
      .then(({ path }) => setDirectoryPath(path))
      .catch((cause) => setDirectoryError(localizeError(cause, t, "app.operationFailed")))
      .finally(() => setDirectoryLoading(false));
  };
  const shellQuote = (value: string) => `'${value.replaceAll("'", `'\\''`)}'`;
  const localCommand = `${session.workspacePath ? `cd -- ${shellQuote(session.workspacePath)} && ` : ""}orni attach ${shellQuote(session.id)}`;

  return (
    <div className="session-workspace session-demo">
      <header className="sess-topbar terminal-mode">
        {onToggleSessionList ? (
          <button
            className="sess-sidebar-toggle"
            type="button"
            onClick={toggleSessionList}
            aria-label={sessionListCollapsed ? t("session.list.show") : t("session.list.hide")}
            title={sessionListCollapsed ? t("session.list.show") : t("session.list.hide")}
          >
            {sessionListCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        ) : (
          <button className="sess-back" type="button" onClick={onBack} aria-label={t("session.back")}><ArrowLeft size={16} /></button>
        )}
        <div className="sess-provider-identity">
          <span className={`dot ${session.state === "waiting" ? "waiting" : session.state === "running" ? "running" : "idle"}`} />
          <ProviderIcon provider={providerIcon(session.provider)} size={13} />
          <span>{session.provider}</span>
        </div>
        <div className="sess-breadcrumb">
          {displayWorkspaceLabel(session.workspaceLabel) ? <><span>{session.workspaceLabel}</span><span>/</span></> : null}
          {renaming && !isExecutionSession ? (
            <span className="session-name-editor">
              <input autoFocus value={nameDraft} maxLength={160} onChange={(event) => setNameDraft(event.target.value)} onKeyDown={(event) => {
                if (event.key === "Enter") saveName();
                if (event.key === "Escape") { setNameDraft(session.title); setRenaming(false); }
              }} />
              <button type="button" onClick={saveName} aria-label={t("session.nameSave")}><Check size={12} /></button>
              <button type="button" onClick={() => { setNameDraft(session.title); setRenaming(false); }} aria-label={t("session.nameCancel")}><X size={12} /></button>
            </span>
          ) : isExecutionSession ? (
            <strong className="session-readonly-title">{displaySessionTitle(session.title, session.provider)}</strong>
          ) : (
            <button className="session-name-button" type="button" onClick={() => setRenaming(true)} title={t("session.rename")}><strong>{displaySessionTitle(session.title, session.provider)}</strong><Pencil size={11} /></button>
          )}
        </div>
        <div className="sess-topbar-spacer" />
        {!isCreationFailure ? <>
          {onOpenSessionSwitcher ? (
            <div className="session-switcher-wrap">
              <button className="icon-button" type="button" title={t("sessions.switchSession")} aria-label={t("sessions.switchSession")} onClick={onOpenSessionSwitcher}>
                <List size={14} />
              </button>
              {onChooseSession && recentSessions.length ? (
                <div className="session-switcher-popover" role="menu" aria-label={t("sessions.switchSession")}>
                  {recentSessions.map((recentSession) => (
                    <button
                      key={recentSession.id}
                      type="button"
                      role="menuitem"
                      className={recentSession.id === session.id ? "active" : ""}
                      onClick={() => onChooseSession(recentSession.id)}
                    >
                      <span className={`session-switcher-status-dot ${sessionStatusTone(recentSession.state)}`} aria-hidden="true" />
                      <span className="session-switcher-copy">
                        <strong>{displaySessionTitle(recentSession.title, recentSession.provider)}</strong>
                        <small>{recentSession.provider}{displayWorkspaceLabel(recentSession.workspaceLabel) ? ` · ${recentSession.workspaceLabel}` : ""}</small>
                      </span>
                      {recentSession.id === session.id ? <Check size={13} aria-label={t("status.running")} /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <button className="icon-button" type="button" title={t("session.more")} aria-label={t("session.more")} aria-expanded={sessionMenuOpen} onClick={() => setSessionMenuOpen((value) => !value)}><MoreHorizontal size={14} /></button>
        </> : null}
      </header>
      <div className="sess-body">
        {isSessionStarting ? <SessionStartingState session={session} /> : isSessionInactive ? <SessionInactiveState session={session} onStart={() => void requestState("running")} /> : <div className="sess-pane session-terminal-fill active">
          <NativeSessionView client={client} session={session} readOnly={isExecutionSession} insertText={insertText} quickCommands={providerQuickCommands} />
        </div>}
      </div>
      {sessionMenuOpen ? <SessionActionsDialog session={session} canStop={canStop} isSessionStarting={isSessionStarting} onClose={() => setSessionMenuOpen(false)} onOpenCreate={() => { setSessionMenuOpen(false); onOpenCreate?.(); }} onOpenImport={() => { setSessionMenuOpen(false); onOpenImport?.(); }} onViewDirectory={() => openDirectoryDialog("view")} onNewDirectory={() => openDirectoryDialog("new")} onStop={() => setPendingState("stop")} onRestart={() => setPendingState("restart")} onLocalCommand={() => setLocalCommandOpen(true)} onDelete={() => { setSessionMenuOpen(false); setDeleteConfirmOpen(true); }} /> : null}
      {pendingState && !isExecutionSession ? <ConfirmDialog title={pendingState === "stop" ? t("session.stopConfirm") : t("session.restartConfirm")} message={pendingState === "stop" ? t("session.stopDetail") : t("session.restartDetail")} confirmLabel={pendingState === "stop" ? t("session.stop") : t("session.restart")} onCancel={() => setPendingState(null)} onConfirm={() => { void requestState(pendingState === "stop" ? "stopped" : "running"); setPendingState(null); }} /> : null}
      {deleteConfirmOpen ? <ConfirmDialog title={t("sessions.deleteSession")} message={t("sessions.deleteConfirm", { title: displaySessionTitle(session.title, session.provider) })} confirmLabel={t("common.delete")} onCancel={() => setDeleteConfirmOpen(false)} onConfirm={() => { setDeleteConfirmOpen(false); onDelete?.(); }} /> : null}
      {localCommandOpen && !isExecutionSession ? <CommandDialog title={t("session.localCommand.title")} description={t("session.localCommand.detail")} command={localCommand} onClose={() => setLocalCommandOpen(false)} /> : null}
      {directoryDialog ? <CurrentDirectoryDialog mode={directoryDialog} path={directoryPath} loading={directoryLoading} error={directoryError} busy={directoryBusy} onClose={() => { if (!directoryBusy) setDirectoryDialog(null); }} onCreate={async (provider) => {
        if (!directoryPath) return;
        setDirectoryBusy(true);
        setDirectoryError(null);
        try { await onNewSessionCurrentDir(provider, directoryPath); setDirectoryDialog(null); }
        catch (cause) { setDirectoryError(localizeError(cause, t, "app.operationFailed")); }
        finally { setDirectoryBusy(false); }
      }} /> : null}
    </div>
  );
}

function SessionActionsDialog({ session, canStop, isSessionStarting, onClose, onOpenCreate, onOpenImport, onViewDirectory, onNewDirectory, onStop, onRestart, onLocalCommand, onDelete }: {
  session: Session;
  canStop: boolean;
  isSessionStarting: boolean;
  onClose: () => void;
  onOpenCreate: () => void;
  onOpenImport: () => void;
  onViewDirectory: () => void;
  onNewDirectory: () => void;
  onStop: () => void;
  onRestart: () => void;
  onLocalCommand: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const action = (run: () => void) => {
    run();
    onClose();
  };
  return <div className="dialog-backdrop session-actions-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="confirm-dialog session-actions-dialog" role="dialog" aria-modal="true" aria-labelledby="session-actions-title">
      <header><div><strong id="session-actions-title">{t("session.more")}</strong><p>{session.provider}</p></div><button type="button" onClick={onClose} aria-label={t("common.close")}><X size={16} /></button></header>
      <div className="session-actions-grid">
        <button type="button" onClick={() => action(onOpenCreate)}><Plus size={15} />{t("sessions.newSession")}</button>
        <button type="button" onClick={() => action(onOpenImport)}><Import size={15} />{t("sessions.importSession")}</button>
        <button type="button" onClick={() => action(onViewDirectory)}><FolderOpen size={15} />{t("session.directory.view")}</button>
        <button type="button" onClick={() => action(onNewDirectory)}><Plus size={15} />{t("session.directory.new")}</button>
        {canStop ? <button type="button" onClick={() => action(onStop)}><CircleStop size={13} />{t("session.stop")}</button> : <button type="button" disabled={isSessionStarting} onClick={() => action(() => onRestart())}><Play size={15} />{t("session.startConnect")}</button>}
        <button type="button" disabled={isSessionStarting} onClick={() => action(onRestart)}><Power size={13} />{t("session.restart")}</button>
        <button type="button" disabled={isSessionStarting} onClick={() => action(onLocalCommand)}><TerminalSquare size={15} />{t("session.localCommandShow")}</button>
        <button type="button" className="danger" onClick={onDelete}><Trash2 size={15} />{t("common.delete")}</button>
      </div>
    </section>
  </div>;
}

function CurrentDirectoryDialog({ mode, path, loading, error, busy, onClose, onCreate }: {
  mode: "view" | "new"; path: string; loading: boolean; error: string | null; busy: boolean;
  onClose: () => void; onCreate: (provider: Session["provider"]) => Promise<void>;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copyPath = async () => {
    if (!path) return;
    try { await navigator.clipboard.writeText(path); }
    catch {
      const field = document.createElement("textarea");
      field.value = path;
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      document.execCommand("copy");
      field.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return <div className="dialog-backdrop confirm-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className="confirm-dialog session-directory-dialog" role="dialog" aria-modal="true" aria-labelledby="session-directory-title">
      <header><div><strong id="session-directory-title">{mode === "view" ? t("session.directory.view") : t("session.directory.new")}</strong><p>{mode === "view" ? t("session.directory.viewDetail") : t("session.directory.newDetail")}</p></div><button type="button" onClick={onClose} aria-label={t("common.close")} disabled={busy}><X size={16} /></button></header>
      <div className="session-directory-body">{loading ? <div className="session-directory-loading"><span className="spinner" />{t("common.loading")}</div> : error ? <p className="session-directory-error">{error}</p> : <><code title={path}>{path || t("create.unknownWorkspace")}</code>{mode === "view" ? <button className="ghost-button" type="button" disabled={!path} onClick={() => void copyPath()}>{copied ? <Check size={13} /> : <Copy size={13} />}{copied ? t("common.copied") : t("common.copyCommand")}</button> : <><span className="session-directory-label">{t("create.provider")}</span><div className="session-directory-providers">{(["Codex", "Claude Code", "OpenCode"] as Session["provider"][]).map((provider) => <button className="ghost-button" type="button" key={provider} disabled={busy || !path} onClick={() => void onCreate(provider)}><ProviderIcon provider={providerIcon(provider)} size={14} />{provider}{busy ? <span className="spinner" /> : <ChevronRight size={13} />}</button>)}</div></>}</>}</div>
      {mode === "view" ? <footer><button className="primary-button" type="button" onClick={onClose}>{t("common.gotIt")}</button></footer> : null}
    </section>
  </div>;
}

function SessionStartingState({ session }: { session: Session }) {
  const { t } = useI18n();
  const creating = Boolean(session.launchingWork);
  const failed = session.state === "error";
  return <div className={`session-starting-state ${failed ? "session-inactive-state" : ""}`} role={failed ? "alert" : "status"} aria-live="polite">
    <span className="session-starting-indicator">{failed ? <ShieldAlert size={17} /> : <span className="spinner" />}</span>
    <h2>{failed ? t("session.starting.createErrorTitle") : creating ? t("session.starting.createTitle") : t("session.starting.providerTitle", { provider: session.provider })}</h2>
    <p>{failed ? session.activity || t("session.starting.createErrorDetail") : creating ? t("session.starting.createDetail") : t("session.starting.providerDetail")}</p>
  </div>;
}

function SessionInactiveState({ session, onStart }: { session: Session; onStart: () => void }) {
  const { t } = useI18n();
  const failed = session.state === "error";
  const exited = session.state === "exited";
  return <div className="session-starting-state session-inactive-state" role="status" aria-live="polite">
    <span className="session-starting-indicator">{failed ? <ShieldAlert size={17} /> : <TerminalSquare size={17} />}</span>
    <h2>{failed ? t("session.inactive.error", { provider: session.provider }) : exited ? t("session.inactive.exited", { provider: session.provider }) : t("session.inactive.title")}</h2>
    <p>{session.workRole === "execution" ? t("session.inactive.workerDetail") : t("session.inactive.detail", { provider: session.provider })}</p>
    {session.workRole !== "execution" ? <button className="primary-button session-start-action" type="button" onClick={onStart}><Play size={14} />{t("session.startConnect")}</button> : null}
  </div>;
}

function isInactiveSessionState(state: Session["state"]): boolean {
  return ["idle", "done", "stopped", "exited", "error"].includes(state);
}

function displayWorkspaceLabel(value: string): boolean {
  return value.trim().length > 0 && !/^ses_[a-z0-9_-]+$/iu.test(value.trim());
}

function displaySessionTitle(value: string, fallback: string): string {
  const title = value.trim();
  return title && !/^ses_[a-z0-9_-]+$/iu.test(title) ? title : fallback;
}

function providerIcon(provider: Session["provider"]): "codex" | "claude" | "opencode" {
  return provider === "Claude Code" ? "claude" : provider === "OpenCode" ? "opencode" : "codex";
}

function sessionStatusTone(state: Session["state"]): string {
  return state === "running" ? "running" : state === "waiting" ? "waiting" : state === "error" ? "error" : state === "starting" ? "starting" : "idle";
}
